import React from 'react';
import {
  Eye,
  Edit2,
  Trash2,
  MoreHorizontal,
  RotateCw,
  Ban
} from 'lucide-react';

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger
} from '../ui/dropdown-menu';
import { Button } from '../ui/button';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger
} from '../ui/alert-dialog';
import {
  isCrawledItem,
  KnowledgeBaseItemResponse
} from '../../types/KnowledgeBaseItemResponse';

function KnowledgeBaseActionsDropdown({
  item: kbItem,
  onView,
  onEdit,
  onDelete,
  onRetry
}: Readonly<{
  item: KnowledgeBaseItemResponse;
  onView: (item: KnowledgeBaseItemResponse) => void;
  onEdit: (item: KnowledgeBaseItemResponse) => void;
  onDelete: (item: KnowledgeBaseItemResponse) => Promise<void>;
  onRetry?: (item: KnowledgeBaseItemResponse) => void;
}>): React.ReactElement {
  const crawled = isCrawledItem(kbItem);
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant='ghost'
          className='h-8 w-8 p-0'
          onClick={(e) => e.stopPropagation()}
        >
          <span className='sr-only'>Open menu</span>
          <MoreHorizontal className='h-4 w-4' />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align='end' onClick={(e) => e.stopPropagation()}>
        <DropdownMenuLabel>Actions</DropdownMenuLabel>
        <DropdownMenuItem
          onClick={(e) => {
            e.stopPropagation();
            onView(kbItem);
          }}
        >
          <Eye className='mr-2 h-4 w-4' />
          View {crawled ? 'page' : 'document'}
        </DropdownMenuItem>
        <DropdownMenuItem
          onClick={(e) => {
            e.stopPropagation();
            onEdit(kbItem);
          }}
        >
          <Edit2 className='mr-2 h-4 w-4' />
          Edit {crawled ? 'page' : 'document'}
        </DropdownMenuItem>
        {kbItem.ingestionStatus === 'FAILED' && onRetry && (
          <DropdownMenuItem
            onClick={(e) => {
              e.stopPropagation();
              onRetry(kbItem);
            }}
          >
            <RotateCw className='mr-2 h-4 w-4' />
            Retry processing
          </DropdownMenuItem>
        )}
        <DropdownMenuSeparator />
        <AlertDialog>
          <AlertDialogTrigger asChild>
            <DropdownMenuItem onSelect={(e) => e.preventDefault()}>
              {crawled ? (
                <>
                  <Ban className='mr-2 h-4 w-4' />
                  Exclude this page
                </>
              ) : (
                <>
                  <Trash2 className='mr-2 h-4 w-4' />
                  Delete document
                </>
              )}
            </DropdownMenuItem>
          </AlertDialogTrigger>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>
                {crawled ? 'Exclude this page?' : 'Are you absolutely sure?'}
              </AlertDialogTitle>
              <AlertDialogDescription>
                {crawled
                  ? `'${kbItem.name}' is removed from the knowledge base and a rule is added to its website so the next crawl does not bring it back.`
                  : `This action cannot be undone. This will permanently delete '${kbItem.name}' from your knowledge base.`}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction onClick={() => onDelete(kbItem)}>
                {crawled ? 'Exclude' : 'Delete'}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export default KnowledgeBaseActionsDropdown;
